import { useState, useEffect, useCallback, useMemo } from 'react';
import { snapToFrame, getFrameTolerance } from '../utils/timeUtils';
import { getFPS, COURT_KEYPOINT_LABELS, COURT_KEYPOINT_TYPES } from '../utils/constants';
import { getFrameForTime, getTimeForFrame } from '../utils/frameIndex';
import { parseJSONAsync } from '../utils/annotationUtils';

export const useVideoMode = (currentTime, playerRef) => {
  const [courtKeypoints, setCourtKeypoints] = useState([]);
  const [videoMode, setVideoMode] = useState('ball'); // 'ball', 'court', 'player', or 'rally'
  const [currentCourtKeypoints, setCurrentCourtKeypoints] = useState(null);

  // Helper getters for backward compatibility
  const isCourtMode = videoMode === 'court';
  const isPlayerMode = videoMode === 'player';
  const isBallMode = videoMode === 'ball';
  const isRallyMode = videoMode === 'rally';

  // Frame-indexed Map for O(1) per-frame lookups instead of O(n) .filter() on every tick
  const courtKeypointsByFrame = useMemo(() => {
    if (courtKeypoints.length === 0) return new Map();
    const map = new Map();
    for (const court of courtKeypoints) {
      const frame = getFrameForTime(court.timestamp);
      if (!map.has(frame)) map.set(frame, []);
      map.get(frame).push(court);
    }
    return map;
  }, [courtKeypoints]);

  // Recalculate current court keypoints when video time changes
  useEffect(() => {
    if (!playerRef.current) return;
    
    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);
    
    const courts = courtKeypointsByFrame.get(currentFrame);
    
    // Return all courts at this frame (including reprojected ones)
    setCurrentCourtKeypoints(courts && courts.length > 0 ? courts : null);
  }, [currentTime, courtKeypointsByFrame]);

  // Find the most recent court annotation before current time (prioritize primary courts)
  const findPreviousCourtAnnotation = (currentTime) => {
    const previousCourts = courtKeypoints
      .filter(court => court.timestamp < currentTime)
      .sort((a, b) => b.timestamp - a.timestamp);
    
    // First try to find a primary (non-reprojected) court
    const previousPrimaryCourt = previousCourts.find(court => !court.reprojected);
    if (previousPrimaryCourt) {
      return previousPrimaryCourt;
    }
    
    // Fallback to any court if no primary court exists
    return previousCourts.length > 0 ? previousCourts[0] : null;
  };

  // Initialize court keypoints with previous annotation (no defaults)
  const initializeCourtKeypoints = (currentTime) => {
    const previousCourt = findPreviousCourtAnnotation(currentTime);
    
    if (previousCourt) {
      // Use previous court positions as starting point, ensuring backward compatibility
      const keypointsWithOutOfFrame = {};
      Object.keys(previousCourt.keypoints).forEach(label => {
        const keypoint = previousCourt.keypoints[label];
        keypointsWithOutOfFrame[label] = {
          ...keypoint,
          outOfFrame: keypoint.outOfFrame !== undefined ? keypoint.outOfFrame : false
        };
      });
      return keypointsWithOutOfFrame;
    } else {
      // No fallback - return null if no previous court exists
      return null;
    }
  };

  // Enter court mode
  const enterCourtMode = () => {
    setVideoMode('court');
  };

  // Exit court mode
  const exitCourtMode = () => {
    setVideoMode('ball');
  };

  // Enter player mode
  const enterPlayerMode = () => {
    setVideoMode('player');
  };

  // Exit player mode
  const exitPlayerMode = () => {
    setVideoMode('ball');
  };

  const setVideoModeDirect = useCallback((mode) => {
    if (['ball', 'court', 'player', 'rally'].includes(mode)) {
      setVideoMode(mode);
    }
  }, []);

  // Get current court keypoints for display
  const getCurrentCourtKeypoints = () => {
    if (!playerRef.current) return null;
    
    const videoCurrentTime = playerRef.current.currentTime();
    return initializeCourtKeypoints(videoCurrentTime);
  };

  // Save court keypoints for current frame
  const saveCourtKeypoints = (keypoints) => {
    if (!playerRef.current) return;
    
    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);
    const snappedTime = getTimeForFrame(currentFrame);
    
    // Find all courts at this frame
    const courtsAtCurrentTime = courtKeypoints.filter(court => 
      getFrameForTime(court.timestamp) === currentFrame
    );
    
    // Find existing primary (non-reprojected) court
    const existingPrimaryCourt = courtsAtCurrentTime.find(court => !court.reprojected);
    
    const newPrimaryCourtAnnotation = {
      id: existingPrimaryCourt ? existingPrimaryCourt.id : Date.now(),
      timestamp: snappedTime,
      keypoints: keypoints,
      reprojected: false // User-modified courts are not reprojected
    };

    if (existingPrimaryCourt) {
      // Update only the primary court, preserve all reprojected courts
      setCourtKeypoints(prev => prev.map(court => 
        court.id === existingPrimaryCourt.id
          ? newPrimaryCourtAnnotation
          : court
      ));
    } else {
      // Add new primary court annotation (reprojected courts at this time are preserved)
      setCourtKeypoints(prev => [...prev, newPrimaryCourtAnnotation]);
    }
  };

  // Remove court keypoints at current time (only removes primary court, preserves reprojected)
  const removeCourtKeypoints = () => {
    if (!playerRef.current) return;
    
    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);
    
    // Only remove primary (non-reprojected) courts at current time
    setCourtKeypoints(prev => prev.filter(court => 
      getFrameForTime(court.timestamp) !== currentFrame || court.reprojected
    ));
  };

  // Get court keypoint counts (only non-reprojected courts)
  const getCourtKeypointCounts = () => {
    return courtKeypoints.filter(court => !court.reprojected).length;
  };

  // Save court keypoints to JSON
  const saveCourtKeypointsToJSON = (videoUrl, videoDuration) => {
    if (!playerRef.current) return;
    
    const currentFPS = getFPS();
    if (!currentFPS) {
      console.error('Cannot save court keypoints: FPS not set');
      return;
    }
    
    const actualVideoWidth = playerRef.current?.videoWidth || 1280;
    const actualVideoHeight = playerRef.current?.videoHeight || 720;
    
    let videoFilename = '';
    try {
      if (videoUrl) {
        const urlWithoutParams = videoUrl.split('?')[0];
        const urlParts = urlWithoutParams.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        if (lastPart.toLowerCase().endsWith('.mp4')) {
          videoFilename = lastPart;
        }
      }
    } catch (error) {
      console.warn('Could not extract video filename from URL:', error);
    }

    // Filter out reprojected keypoints before saving
    // Convert exact PTS back to frame/fps format for file compatibility
    const courtKeypointsData = courtKeypoints
      .filter(court => !court.reprojected)
      .map(court => {
        const frame = getFrameForTime(court.timestamp);
        const fileTime = currentFPS ? frame / currentFPS : court.timestamp;
        return {
          time: fileTime,
          timestamp: formatTime(fileTime),
        keypoints: court.keypoints
        };
      });

    const data = {
      video_filename: videoFilename,
      video_width: actualVideoWidth,
      video_height: actualVideoHeight,
      fps: currentFPS,
      total_frames: Math.floor(videoDuration * currentFPS),
      total_duration: formatTime(videoDuration),
      court_keypoints: courtKeypointsData
    };

    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const a = document.createElement('a');
    a.href = url;
    a.download = videoFilename ? `${videoFilename}_court_keypoints_${timestamp}.json` : `court_keypoints_${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load court keypoints from JSON
  const loadCourtKeypoints = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = await parseJSONAsync(event.target.result);
          if (!Array.isArray(data.court_keypoints)) throw new Error('No court_keypoints array');
          
          const loadedCourtKeypoints = data.court_keypoints.map((court, i) => {
            const fps = data.fps || getFPS();
            if (!fps) {
              throw new Error('No FPS value found in court keypoints file and no FPS set in app');
            }
            
            // Recover frame number from time, then look up exact PTS
            const frameNumber = Math.round((court.time || 0) * fps);
            let timestamp = getTimeForFrame(frameNumber);
            
            // Handle multiple courts in the same frame - check if this is an array of courts
            const courtsAtFrame = Array.isArray(court.keypoints) ? court.keypoints : [court];
            
            // Process each court at this frame
            const processedCourts = courtsAtFrame.map((singleCourt, courtIndex) => {
              // Ensure backward compatibility by adding outOfFrame property to keypoints
              const keypointsWithOutOfFrame = {};
              const courtKeypoints = singleCourt.keypoints || singleCourt;
              
              if (courtKeypoints && typeof courtKeypoints === 'object') {
                Object.keys(courtKeypoints).forEach(label => {
                  const keypoint = courtKeypoints[label];
                  if (keypoint && typeof keypoint === 'object' && 'x' in keypoint && 'y' in keypoint) {
                    keypointsWithOutOfFrame[label] = {
                      ...keypoint,
                      outOfFrame: keypoint.outOfFrame !== undefined ? keypoint.outOfFrame : false
                    };
                  }
                });
              }
              
              return {
                id: `${court.id || Date.now() + i}-${courtIndex}`,
                timestamp: timestamp,
                keypoints: keypointsWithOutOfFrame,
                reprojected: singleCourt.reprojected || false
              };
            });
            
            return processedCourts;
          }).flat(); // Flatten the array since we might have multiple courts per frame
          
          setCourtKeypoints(loadedCourtKeypoints);
          resolve({ success: true, filename: file.name });
        } catch (error) {
          console.error('Error loading court keypoints:', error);
          resolve({ success: false, error: 'Invalid court keypoints file format.' });
        }
      };
      reader.readAsText(file);
    });
  };

  // Clear all court keypoints
  const clearCourtKeypoints = () => {
    setCourtKeypoints([]);
  };

  // Copy previous court annotation to current time
  const copyPreviousCourtAnnotation = () => {
    if (!playerRef.current) return false;
    
    const videoCurrentTime = playerRef.current.currentTime();
    const previousCourt = findPreviousCourtAnnotation(videoCurrentTime);
    
    if (!previousCourt) {
      console.warn('No previous court annotation found to copy');
      return false;
    }
    
    // Copy the previous court annotation to current frame
    const currentFrame = getFrameForTime(videoCurrentTime);
    const snappedTime = getTimeForFrame(currentFrame);
    
    // Check if there's already a court annotation at current frame
    const courtsAtCurrentTime = courtKeypoints.filter(court => 
      getFrameForTime(court.timestamp) === currentFrame
    );
    
    // Find existing primary (non-reprojected) court
    const existingPrimaryCourt = courtsAtCurrentTime.find(court => !court.reprojected);
    
    const copiedCourtAnnotation = {
      id: existingPrimaryCourt ? existingPrimaryCourt.id : Date.now(),
      timestamp: snappedTime,
      keypoints: { ...previousCourt.keypoints }, // Deep copy the keypoints
      reprojected: false // User-copied courts are not reprojected
    };

    if (existingPrimaryCourt) {
      // Update only the primary court, preserve all reprojected courts
      setCourtKeypoints(prev => prev.map(court => 
        court.id === existingPrimaryCourt.id
          ? copiedCourtAnnotation
          : court
      ));
    } else {
      // Add new primary court annotation (reprojected courts at this time are preserved)
      setCourtKeypoints(prev => [...prev, copiedCourtAnnotation]);
    }
    
    return true;
  };

  // Helper function to format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  return {
    courtKeypoints,
    courtKeypointsByFrame,
    setCourtKeypoints,
    videoMode,
    isCourtMode,
    isPlayerMode,
    isBallMode,
    isRallyMode,
    currentCourtKeypoints,
    enterCourtMode,
    exitCourtMode,
    enterPlayerMode,
    exitPlayerMode,
    setVideoModeDirect,
    saveCourtKeypoints,
    removeCourtKeypoints,
    getCourtKeypointCounts,
    saveCourtKeypointsToJSON,
    loadCourtKeypoints,
    clearCourtKeypoints,
    findPreviousCourtAnnotation,
    initializeCourtKeypoints,
    getCurrentCourtKeypoints,
    copyPreviousCourtAnnotation
  };
}; 